/**
 * Registers the PokemonDPPt pixel font on `document.fonts` so every
 * Shadow DOM instance can use it.
 *
 * Why not `@font-face` inside the shadow stylesheet?
 * Browsers treat `@font-face` declarations nested inside a Shadow DOM
 * inconsistently — Chrome honors them in some cases, Safari doesn't
 * load them at all. Registering via `document.fonts.add(new FontFace(...))`
 * installs the font at the document level; shadow roots inherit it and
 * a single `font-family: "PokemonDPPt"` reference works everywhere.
 *
 * Idempotent: if the font has already been added (e.g. a second
 * `<koko-companion>` instance mounts), we skip the fetch.
 */

const FONT_FAMILY = "PokemonDPPt";

let loadPromise: Promise<void> | null = null;

export function loadKokoFont(fontUrl: string): Promise<void> {
  if (loadPromise) return loadPromise;

  if (typeof document === "undefined" || !("fonts" in document)) {
    return Promise.resolve();
  }

  // If a page has already registered the font (multiple Koko instances,
  // or a host site embedding the same font), we don't need to fetch again.
  const existingFontLoaded = Array.from(document.fonts).some(
    (font) => font.family === FONT_FAMILY && font.status === "loaded",
  );
  if (existingFontLoaded) {
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  const fontFace = new FontFace(FONT_FAMILY, `url("${fontUrl}") format("truetype")`, {
    display: "block",
  });

  loadPromise = fontFace
    .load()
    .then((loadedFont) => {
      document.fonts.add(loadedFont);
    })
    .catch((error) => {
      console.warn("[koko] Failed to load PokemonDPPt font:", error);
    });

  return loadPromise;
}
